import { Record as record, List } from 'immutable';
import _ from 'lodash';

import config from '../../../../src/config';
import { construct, resolve, fields } from '../../../../src/helpers/LinkMapper';
import { getDefaults, getResolvers } from '../../../../src/helpers/Records';
import ValidationMixin from '../../../../src/records/ValidationMixin';
import validators from '../../../../src/validation';

import NonSeasonTicket from './NonSeasonTicket';

const fieldSchema = {
    date: [fields.datetime],
    outward: [resolve.as(NonSeasonTicket), new NonSeasonTicket()],
    return: [resolve.as(NonSeasonTicket)]
};

export default class DayTickets extends record(getDefaults(fieldSchema)) {
    static fromJSON(json = {}) {
        return construct(DayTickets, json, getResolvers(fieldSchema));
    }

    getComputedValues() {
        const getOutwardAndReturn = field => {
            return [
                _.get(this, ['outward', field]),
                _.get(this, ['return', field])
            ];
        };
        const [outwardRoute, returnRoute] = getOutwardAndReturn('route');
        const [outwardBookSize, returnBookSize] = getOutwardAndReturn(
            'bookSize'
        );

        return {
            date: _.result(this, 'date.isValid'),
            outward: _.result(this, 'outward.isComplete'),
            return: _.result(this, 'return.isComplete'),
            returnBookSize:
                outwardBookSize && outwardBookSize === returnBookSize,
            returnRouteMatch:
                outwardRoute && outwardRoute.isComplementary(returnRoute),
            returnEligibility:
                !(
                    _.result(outwardRoute, 'isEligible') &&
                    _.result(returnRoute, 'isEligible')
                ) ||
                _.result(outwardRoute, 'getCompensationLevel') ===
                    _.result(returnRoute, 'getCompensationLevel')
        };
    }

    getSchema() {
        const checkReturnTicket =
            this.outward.mustPair() || (this.outward.canPair() && this.return);
        const returnMustMatch =
            this.outward &&
            this.outward.accepts('returnMustMatch') &&
            this.return;
        return {
            date: validators.true(),
            outward: validators.true(),
            return: checkReturnTicket
                ? validators.true()
                : validators.optional(),
            returnEligibility: checkReturnTicket
                ? validators.true()
                : validators.optional(),
            returnBookSize:
                returnMustMatch && checkReturnTicket
                    ? validators.true()
                    : validators.optional(),
            returnRouteMatch:
                returnMustMatch && checkReturnTicket
                    ? validators.true()
                    : validators.optional()
        };
    }

    getRoutes() {
        return List.of(
            this.outward.getRoute(),
            this.return && this.return.getRoute()
        ).filter(Boolean);
    }

    getOysterCards() {
        return List.of(
            this.outward.accepts('oysterNumber') && this.outward.cardNumber,
            this.return &&
                this.return.accepts('oysterNumber') &&
                this.return.cardNumber
        ).filter(Boolean);
    }

    getTicketTypes() {
        return config.get('compensationNonSeasonTickets');
    }

    getReturnTicketTypes() {
        const allTypes = this.getTicketTypes();
        const { ticketType: outwardType } = this.outward;

        return outwardType
            ? allTypes.filter(t => t.pairsWith(outwardType))
            : allTypes;
    }

    getTicketType(direction) {
        return this.getIn([direction, 'ticketType']);
    }

    addReturn() {
        return this.set('return', new NonSeasonTicket());
    }

    removeReturn() {
        return this.set('return', null);
    }

    hasWeeklyExclusive() {
        const outwardType = this.getTicketType('outward');
        const returnType = this.getTicketType('return');
        return (
            (outwardType && outwardType.weeklyExclusive) ||
            (returnType && returnType.weeklyExclusive)
        );
    }

    hasTypeId(ticketTypeId) {
        const outwardType = this.getTicketType('outward');
        const returnType = this.getTicketType('return');
        return (
            (outwardType && outwardType.id === ticketTypeId) ||
            (returnType && returnType.id === ticketTypeId)
        );
    }

    isLevel2() {
        return (
            _.result(this, 'outward.route.isLevel2') ||
            _.result(this, 'return.route.isLevel2')
        );
    }

    hasNonCircularRoute() {
        const outwardRoute = _.get(this, 'outward.route');
        const returnRoute = _.get(this, 'return.route');
        return (
            outwardRoute &&
            returnRoute &&
            outwardRoute.canSearch() &&
            returnRoute.canSearch() &&
            !outwardRoute.isComplementary(returnRoute)
        );
    }
}

Object.assign(DayTickets.prototype, ValidationMixin);
